import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { VpnKey } from '@mui/icons-material';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import {
  useCreateApiKeyMutation,
  useGetApiKeysQuery,
  useGetMerchantQuery,
} from '../../store/modules/merchant/api';
import ApiKeyCard from './ApiKeyCard';
import ApiKeyCreatedModal from './ApiKeyCreatedModal';
import { isServerError } from '../../util/helpers';
import { toast } from '../../util/toast';
import { Button } from '../../components/core';
import CopyButton from '../../components/core/CopyButton';
import { EmptyPage } from '../../common/EmptyPage';

const handleCreateApiKeyError = (err: unknown) => {
  let errMessage = 'An error occurred while processing your request. Please try again later.';
  if (isServerError(err) && err.status === 422) {
    errMessage = 'Maximum number of API keys reached';
  }
  toast.error(errMessage);
};

const ApiKeys = () => {
  const { merchantId, canUpdateMerchantSettings } = useMerchant();
  const { data: merchantData, isLoading: isLoadingHmacApiKey } = useGetMerchantQuery(merchantId, {
    skip: !merchantId,
  });
  const { data: allApiKeys, isLoading: isLoadingApiKeys } = useGetApiKeysQuery(merchantId, {
    skip: !merchantId,
  });
  const apiKeys = allApiKeys?.filter((k) => !k.isInherited) ?? [];
  const hasModifyAccess = canUpdateMerchantSettings();
  const [createApiKey, { isLoading: isLoadingCreateApiKey }] = useCreateApiKeyMutation();
  const [isCreateApiKeyModalOpen, setCreateApiKeyModalOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const showCreateApiKeyButton = apiKeys && apiKeys.length > 0;

  const handleOpenModal = async () => {
    await createApiKey({ merchantId })
      .unwrap()
      .then((response) => {
        setNewApiKey(response.apiKey);
        setCreateApiKeyModalOpen(true);
      })
      .catch((err) => handleCreateApiKeyError(err));
  };
  const handleCloseModal = () => setCreateApiKeyModalOpen(false);

  return (
    <PageWrapper
      data-testid="API Keys"
      title="API Keys"
      description="Use this page to view, create or manage your API Keys."
      isLoading={isLoadingApiKeys || isLoadingHmacApiKey || !merchantId}
    >
      <Box>
        {merchantData?.apiKey && (
          <Box sx={{ mb: 4 }}>
            <Typography component="h2" variant="xl" fontWeight="semibold">
              Legacy HMAC API Key
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '12px', mt: 1 }}>
              This key is only used for authentication using HMAC key.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: { xs: '12px', sm: '16px' } }}>
                {merchantData.apiKey}
              </Typography>
              <CopyButton title="HMAC API Key" copyString={merchantData.apiKey} />
            </Stack>
          </Box>
        )}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 4,
            }}
          >
            <Box sx={{ alignItems: 'baseline', display: 'flex' }}>
              <Typography variant="xl" fontWeight="semibold">
                API Keys
              </Typography>
              <Typography
                sx={{ color: 'text.secondary', marginLeft: 1, fontWeight: 400, fontSize: '12px' }}
              >
                (Optional)
              </Typography>
            </Box>
            {showCreateApiKeyButton && hasModifyAccess && (
              <Button
                text={isLoadingCreateApiKey ? 'Creating...' : 'Create new API Key'}
                startIcon={<VpnKeyIcon sx={{ color: 'white' }} />}
                onClick={handleOpenModal}
                disabled={isLoadingCreateApiKey}
                sx={{ px: 4, minWidth: '230px' }}
              />
            )}
            {/* API Key Create Modal */}
            <ApiKeyCreatedModal
              newApiKey={newApiKey}
              open={isCreateApiKeyModalOpen}
              onClose={handleCloseModal}
            />
          </Box>
        </Box>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 5,
            }}
          >
            {!apiKeys || apiKeys.length === 0 ? (
              <EmptyPage
                icon={<VpnKey />}
                title="You haven't created any API Keys yet."
                paragraph="Skipify will authenticate your API requests using the API keys you create. Once API keys are created, you will be able to manage them from this page."
                buttonText="Create new API Key"
                onClick={handleOpenModal}
              />
            ) : (
              apiKeys.map((apiKey) => <ApiKeyCard apiKey={apiKey} key={apiKey.apiKeyId} />)
            )}
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default ApiKeys;
